/**
 * Twenty-Nine bot.
 *
 * - Bidding: estimates the card points the team collects with each suit as trump from the first four cards (points
 *   in hand, trump length, the jack and nine of trump), adds what the other cards are worth on average, and bids
 *   just above the standing bid when that reaches it; it leaves a bid its partner already holds unless clearly stronger.
 * - Trump: the suit with the best estimate.
 * - Play: partnership-aware. Every card point goes to one of the two teams, so it feeds winning partners (smearing),
 *   wins point-rich tricks as cheaply as possible, never wastes point cards on a trick it cannot win, and asks for the
 *   hidden trump only when that wins something. NORMAL and above track played cards to recognise master cards.
 * The bot only ever reads its own seat's view.
 */
import { parseCard, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  twentyNineBestSuit,
  twentyNineDeck,
  twentyNinePartnerOf,
  twentyNinePoints,
  twentyNineStrength,
  twentyNineTeamOf,
  twentyNineTrickPoints,
  twentyNineTrickWinner,
  type TwentyNineAction,
  type TwentyNineLegal,
  type TwentyNineTrickPlay,
  type TwentyNineView,
} from './engine.js';

/** Card points the team is expected to collect with `trump`, judged from the first four cards. */
export function estimateTwentyNine(hand: readonly CardId[], trump: Suit): number {
  const cards = hand.slice(0, 4);
  const points = cards.reduce((n, c) => n + twentyNinePoints(c), 0);
  const trumps = cards.filter((c) => parseCard(c).suit === trump);
  const has = (rank: number) => trumps.some((c) => parseCard(c).rank === rank);
  let est = 11 + points + 1.4 * Math.max(0, trumps.length - 1);
  if (has(11)) est += 1.5;
  if (has(9)) est += 0.8;
  return est;
}

function bestTrump(hand: readonly CardId[]): { suit: Suit; est: number } {
  let suit = twentyNineBestSuit(hand);
  let est = estimateTwentyNine(hand, suit);
  for (const s of SUITS) {
    const e = estimateTwentyNine(hand, s);
    if (e > est + 0.05) {
      est = e;
      suit = s;
    }
  }
  return { suit, est };
}

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: TwentyNineView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const remembered = ctx.profile.memory;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (remembered >= 1 || i >= view.tricks.length - Math.max(1, Math.round(view.tricks.length * remembered))) {
      for (const p of t.plays) seen.add(p.card);
    }
  });
  return { unseen: new Set(twentyNineDeck().filter((c) => !seen.has(c))) };
}

/** True when no unseen card of the same suit is stronger. */
function isMaster(card: CardId, k: Knowledge): boolean {
  const c = parseCard(card);
  for (const u of k.unseen) {
    if (parseCard(u).suit === c.suit && twentyNineStrength(u) > twentyNineStrength(card)) return false;
  }
  return true;
}

function wouldWin(trick: readonly TwentyNineTrickPlay[], seat: number, card: CardId, trump: Suit | null): boolean {
  if (trick.length === 0) return true;
  return twentyNineTrickWinner([...trick, { seat, card }], trump) === seat;
}

const byCheap = (a: CardId, b: CardId) => twentyNinePoints(a) - twentyNinePoints(b) || twentyNineStrength(a) - twentyNineStrength(b);

function chooseCard(view: TwentyNineView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const me = view.seat!;
  const partner = twentyNinePartnerOf(me);
  const trick = view.trick;
  const live = view.trumpRevealed ? view.trump : null;
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;
  const sorted = [...legal].sort(byCheap);
  const cheapest = sorted[0]!;
  const richest = [...legal].sort((a, b) => twentyNinePoints(b) - twentyNinePoints(a) || twentyNineStrength(a) - twentyNineStrength(b))[0]!;
  const declarerTeam = view.declarer === null ? -1 : twentyNineTeamOf(view.declarer);
  const mine = twentyNineTeamOf(me) === declarerTeam;

  if (trick.length === 0) {
    // Lead a master (jacks first) from a side suit, else trump when we hold a dominant trump suit, else a cheap card.
    const isTrumpCard = (c: CardId) => live !== null && parseCard(c).suit === live;
    const masters = legal.filter((c) => (k ? isMaster(c, k) : parseCard(c).rank === 11));
    const sideMasters = masters.filter((c) => !isTrumpCard(c));
    if (mine && live) {
      const trumps = legal.filter(isTrumpCard);
      const topTrumps = trumps.filter((c) => (k ? isMaster(c, k) : parseCard(c).rank === 11));
      if (topTrumps.length > 0 && trumps.length >= 3) return [...topTrumps].sort((a, b) => twentyNineStrength(b) - twentyNineStrength(a))[0]!;
    }
    if (sideMasters.length > 0) return [...sideMasters].sort((a, b) => twentyNinePoints(b) - twentyNinePoints(a) || twentyNineStrength(b) - twentyNineStrength(a))[0]!;
    // Lead low from a suit where a partner jack may sit; avoid exposing point cards.
    const nonTrump = sorted.filter((c) => !isTrumpCard(c));
    return (nonTrump.length > 0 ? nonTrump : sorted)[0]!;
  }

  const winnerSeat = twentyNineTrickWinner(trick, live);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const last = trick.length === 3;
  const trickPts = twentyNineTrickPoints(trick);
  const winners = legal.filter((c) => wouldWin(trick, me, c, live));

  if (winnerSeat === partner) {
    // The partner leads the trick. Feed it points when it is safe, otherwise stay cheap.
    const winnerIsTrump = live !== null && parseCard(winnerCard).suit === live;
    const trumpThreat = !winnerIsTrump && live !== null && !last;
    const secure = last || (k ? isMaster(winnerCard, k) && !trumpThreat : parseCard(winnerCard).rank === 11 && !trumpThreat);
    if (secure) return richest;
    // Not safe: overtake when it is cheap and the trick is worth something, else stay low.
    if (winners.length > 0 && trickPts + twentyNinePoints([...winners].sort(byCheap)[0]!) >= 2) return [...winners].sort(byCheap)[0]!;
    return cheapest;
  }

  if (winners.length === 0) return cheapest;
  const cheapWinner = [...winners].sort((a, b) => twentyNineStrength(a) - twentyNineStrength(b))[0]!;
  const worth = trickPts + twentyNinePoints(cheapWinner);
  if (last) return worth >= 1 || winners.length === legal.length ? cheapWinner : cheapest;
  // Not last: take it with a card that cannot be beaten when possible, and skip worthless tricks.
  const safe = k ? winners.filter((c) => isMaster(c, k) || (live !== null && parseCard(c).suit === live)) : winners;
  if (worth === 0) return cheapest;
  return [...(safe.length ? safe : winners)].sort((a, b) => twentyNineStrength(a) - twentyNineStrength(b))[0]!;
}

/** Whether to ask for the hidden trump when out of the led suit. */
function shouldReveal(view: TwentyNineView, legal: Extract<TwentyNineLegal, { kind: 'play' }>, ctx: BotContext): boolean {
  if (legal.cards.length === 0) return true;
  const me = view.seat!;
  const trick = view.trick;
  const winnerSeat = twentyNineTrickWinner(trick, null);
  if (winnerSeat === twentyNinePartnerOf(me)) return false;
  const pts = twentyNineTrickPoints(trick);
  if (view.trump !== null) {
    // The bidder knows the suit and ruffs when it wins something.
    const trumps = view.hand.filter((c) => parseCard(c).suit === view.trump);
    return trumps.length > 0 && (pts >= 1 || trumps.length >= 3 || chance(ctx, 0.3));
  }
  return pts >= 2 || (pts >= 1 && chance(ctx, 0.4));
}

export const twentyNineBot: BotEngine<TwentyNineView, TwentyNineLegal, TwentyNineAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bid') return this.chooseBet!(view, legal, ctx);
    if (legal.kind === 'trump') return { type: 'trump', suit: bestTrump(view.hand).suit };
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid') return this.handleTimeout(view, legal, ctx);
    const me = view.seat!;
    let est = bestTrump(view.hand).est;
    if (ctx.difficulty === 'BEGINNER') est += (ctx.rng.next() - 0.5) * 5;
    else if (ctx.difficulty === 'EASY') est += (ctx.rng.next() - 0.5) * 3;
    const shade = ctx.difficulty === 'HARD' || ctx.difficulty === 'EXPERT' ? 1.2 : 0.6;
    const ceiling = Math.min(legal.max, Math.floor(est - shade));
    // Leave the bid with a partner who already holds it unless we are clearly stronger.
    if (view.highBid?.seat === twentyNinePartnerOf(me) && ceiling < legal.min + 2) return legal.canPass ? { type: 'pass' } : { type: 'bid', value: legal.min };
    if (ceiling >= legal.min) return { type: 'bid', value: Math.min(ceiling, legal.min + 1) };
    return legal.canPass ? { type: 'pass' } : { type: 'bid', value: legal.min };
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    if (legal.canReveal && shouldReveal(view, legal, ctx)) return { type: 'reveal' };
    const cards = legal.cards;
    if (cards.length === 0) return { type: 'reveal' };
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = chooseCard(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 14));
  },

  calculateRisk(view, legal) {
    if (legal.kind === 'bid') return Math.min(1, Math.max(0, (legal.min - bestTrump(view.hand).est + 4) / 14));
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid') return legal.canPass ? { type: 'pass' } : { type: 'bid', value: legal.min };
    if (legal.kind === 'trump') return { type: 'trump', suit: bestTrump(view.hand).suit };
    if (legal.kind === 'play') {
      if (legal.cards.length === 0) return { type: 'reveal' };
      return { type: 'play', card: [...legal.cards].sort(byCheap)[0]! };
    }
    throw new Error('No legal action');
  },
};
